import { Injectable, inject } from '@angular/core';
import { getYear } from 'date-fns';
import { BehaviorSubject, Subject } from 'rxjs';
import { SiaOperator } from '../interfaces/sia-operator';
import { SiaSessionParam } from '../interfaces/sia-session-param';
import { SiaDefaultsService } from './defaults.service';
import { SiaNewRouterService } from './new-router.service';
import { SiaSessionService } from './session.service';

export interface DepartmentChangeNotification {
  departmentId: string | undefined;
  description: string | undefined;
  color: string;
}
@Injectable({
  providedIn: 'root',
})
export class SiaParamsAndPatternsService {
  #routerService = inject(SiaNewRouterService);
  #defaultService = inject(SiaDefaultsService);
  #sessionService = inject(SiaSessionService);

  #paramsUpdated: BehaviorSubject<string> = new BehaviorSubject<string>('');

  #departmentChanged: Subject<DepartmentChangeNotification> =
    new Subject<DepartmentChangeNotification>();
  #themeColorChanged: Subject<string> = new Subject<string>();

  #tabsParamsUpdated: BehaviorSubject<string> = new BehaviorSubject<string>('');
  #filterPanelParamsUpdated: BehaviorSubject<string> =
    new BehaviorSubject<string>('');

  #firstFilterLoaded: { [key: string]: boolean } = {};
  #firstFilterLoadedSub: BehaviorSubject<{ [key: string]: boolean }> =
    new BehaviorSubject<{ [key: string]: boolean }>({});

  operators: SiaOperator[] = [
    { id: '', name: 'Nessuno' },
    { id: '<', name: '<' },
    { id: '<=', name: '<=' },
    { id: '>', name: '>' },
    { id: '>=', name: '>=' },
    { id: '=', name: '=' }, // non metto == perchè sql si aspetta solo =
    { id: '!=', name: '<>' },
    { id: 'notcontains', name: 'Non contiene' },
    { id: 'contains', name: 'Contiene' },
    { id: 'startswith', name: 'Inizia per' },
    { id: 'anyof', name: 'IN' },
    { id: 'endswith', name: 'Termina con' },
  ];

  get paramsHardUpdated$() {
    return this.#paramsUpdated.asObservable();
  }

  get firstFilterLoaded$() {
    return this.#firstFilterLoadedSub.asObservable();
  }

  get departmentChanged$() {
    return this.#departmentChanged.asObservable();
  }

  get themeColorChanged$() {
    return this.#themeColorChanged.asObservable();
  }

  get tabParamsUpdated$() {
    return this.#tabsParamsUpdated.asObservable();
  }
  get filterPanelParamsUpdated$() {
    return this.#filterPanelParamsUpdated.asObservable();
  }
  get department() {
    return window.localStorage.getItem('department');
  }

  get departmentDescription() {
    return window.localStorage.getItem('department-description');
  }

  resetDepartment() {
    window.localStorage.removeItem('department');
    window.localStorage.removeItem('department-description');
    this.notifyDepartmentChanged({
      departmentId: undefined,
      description: undefined,
      color: 'default',
    });
  }

  notifyHardParamsUpdated(guid?: string | null) {
    if (!guid) {
      guid =
        this.#routerService.routingData[
          this.#routerService.currentOutletsGuid['base']
        ].guid ?? '';
    }
    this.notifyHardParamsUpdatedWithGuid(guid);
  }

  notifyHardParamsUpdatedWithGuid(guid?: string) {
    if (!guid) return;
    this.#firstFilterLoaded[guid] = true;
    this.#firstFilterLoadedSub.next({ ...this.#firstFilterLoaded });
    this.#paramsUpdated.next(guid);
  }

  notifyParamsUpdatedWithGuidTabs(guid?: string) {
    if (!guid) return;
    this.#tabsParamsUpdated.next(guid);
  }

  notifyParamsUpdatedWithGuidFilterPanel(guid?: string) {
    if (!guid) return;
    this.#filterPanelParamsUpdated.next(guid);
  }

  notifyDepartmentChanged(notification: DepartmentChangeNotification) {
    this.#routerService.closeOutlet('drawer');
    this.#departmentChanged.next(notification);
    this.#themeColorChanged.next(notification.color || 'default');
  }

  /**
   * @ngdoc method
   * @name getItemValueFromParamPattern
   * @description extract the value from the item []
   * @param {string} params the patter to check
   * @param {any} item the object for resolve the []
   * @returns {item}  the resolved value
   */
  private getItemValueFromParamPattern(paramPattern: string, item: any): any {
    if (!item) return;
    var pNoParentesis = paramPattern.split('[').join('');
    pNoParentesis = pNoParentesis.split(']').join('');
    if (pNoParentesis) {
      var lc = pNoParentesis.toLowerCase();
      var value = item[lc];

      if ((value == null || value == undefined) && item) {
        try {
          var itemsAsParams: SiaSessionParam[] = JSON.parse(
            JSON.stringify(item),
          );
          if (!itemsAsParams) return value;
          if (JSON.stringify(itemsAsParams) == JSON.stringify({})) return value;
          var itemParam = itemsAsParams.filter(
            (x) => x.id && x.id.toLowerCase() == lc,
          );
          if (itemParam && itemParam.length == 1) {
            value = itemParam[0].value;
          }
        } catch (error) {
          return null;
        }
      }
      return value;
    }
  }

  /**
   * @ngdoc method
   * @name resolvePatternValue
   * @description resolve a pattern like $id, [name], #today
   * @param {string} singleParamPattern the patter to check
   * @param {any} item the object for resolve the []
   * @param {string} guid to retrieve the session values and resolve $ patterns
   * @returns {item}  the resolved value
   */
  resolvePatternValue(
    singleParamPattern: string,
    item: any,
    guid: string,
    toLowerCase = true,
  ): any {
    singleParamPattern = singleParamPattern.trim();
    if (!singleParamPattern) return singleParamPattern;
    singleParamPattern = singleParamPattern.toLowerCase();
    if (singleParamPattern.startsWith('$')) {
      //devo recuperare un valore dalla sessione
      return this.getSessionParamValue(singleParamPattern, guid);
    } else if (singleParamPattern.startsWith('[')) {
      //devo recuperare un valore da una proprietà dell'oggetto item
      var value = this.getItemValueFromParamPattern(singleParamPattern, item);
      if (typeof value == 'string') {
        value = value;
        if (toLowerCase) {
          value = value.toLowerCase();
        }
      }
      return value;
    } else if (singleParamPattern.startsWith('#')) {
      if (singleParamPattern.indexOf('(number)') > 0) {
        return this.#defaultService.generateDefault(
          'number',
          singleParamPattern.replace('(number)', ''),
        );
      } else if (singleParamPattern.indexOf('(bool)') > 0) {
        return this.#defaultService.generateDefault(
          'bool',
          singleParamPattern.replace('(bool)', ''),
        );
      } else if (singleParamPattern.indexOf('(time)') > 0) {
        return this.#defaultService.generateDefault(
          'time',
          singleParamPattern.replace('(time)', ''),
        );
      } else if (singleParamPattern.indexOf('(date)') > 0) {
        return this.#defaultService.generateDefault(
          'date',
          singleParamPattern.replace('(date)', ''),
        );
      }
      var value = this.#defaultService.generateDefault(
        'text',
        singleParamPattern,
      );
      if (typeof value == 'string') {
        value = value.toLowerCase();
        if (toLowerCase) {
          value = value.toLowerCase();
        }
      }
      return value;
    } else {
      try {
        var value = JSON.parse(singleParamPattern);
        return value;
      } catch (e) {
        return singleParamPattern;
      }
    }
  }
  resolveSquaresInString(value: string, item: any): string {
    if (value.indexOf('[') != -1 && value.indexOf(']') != -1) {
      let endParamIndex = value.indexOf(']');
      let param = value.substring(value.indexOf('['), endParamIndex + 1);
      value = value.replace(param, this.resolvePatternValue(param, item, ''));
      return this.resolveSquaresInString(value, item);
    } else {
      return value;
    }
  }
  /**
   * @ngdoc method
   * @name resolvePatternValue
   * @description resolve a pattern like $id, [name], #today
   * @param {string} singleParamPattern the patter to check
   * @param {any} item the object for resolve the []
   * @param {string} guid to retrieve the session values and resolve $ patterns
   * @returns {item}  the resolved value
   */
  resolvePatternValueNoJson(
    singleParamPattern: string,
    item: any,
    guid: string,
  ): any {
    singleParamPattern = singleParamPattern.trim();
    if (!singleParamPattern) return singleParamPattern;
    if (singleParamPattern.startsWith('$')) {
      //devo recuperare un valore dalla sessione
      return this.getSessionParamValue(singleParamPattern, guid);
    } else if (singleParamPattern.startsWith('[')) {
      //devo recuperare un valore da una proprietà dell'oggetto item
      return this.getItemValueFromParamPattern(singleParamPattern, item);
    } else if (singleParamPattern.startsWith('#')) {
      if (singleParamPattern.indexOf('(number)') > 0) {
        return this.#defaultService.generateDefault(
          'number',
          singleParamPattern.replace('(number)', ''),
        );
      } else if (singleParamPattern.indexOf('(bool)') > 0) {
        return this.#defaultService.generateDefault(
          'bool',
          singleParamPattern.replace('(bool)', ''),
        );
      } else if (singleParamPattern.indexOf('(time)') > 0) {
        return this.#defaultService.generateDefault(
          'time',
          singleParamPattern.replace('(time)', ''),
        );
      } else if (singleParamPattern.indexOf('(date)') > 0) {
        return this.#defaultService.generateDefault(
          'date',
          singleParamPattern.replace('(date)', ''),
        );
      }
      return this.#defaultService.generateDefault('text', singleParamPattern);
    } else {
      return singleParamPattern;
    }
  }
  /**
   * @ngdoc method
   * @name geSiaSessionParamValue
   * @description resolve a session pattern like $id, $name, $title
   * @param {string} singleParamPattern the patter to check
   * @param {string} guid to retrieve the session values and resolve $ patterns
   * @returns {item}  the resolved value
   */
  getSessionParamValue(singleParamPattern: string, guid: string): any {
    var res = this.#sessionService.getSessionParamValue(
      singleParamPattern,
      guid,
    );
    return res;
  }

  resolveMenuLinkPattern(pattern: string, item: any): string {
    if (!pattern) return '';
    if (!item) return '';
    var parts = pattern.split('|');
    if (!parts || parts.length == 0) return '';
    if (!item) return '';
    var res: string[] = [];
    parts.forEach((p) => {
      var value = this.getItemValueFromParamPattern(p, item);
      if (value) {
        res.push(value);
      }
    });

    if (!res || res.length == 0) return '';
    var resolved: any[] = [];
    res.forEach((r) => {
      var concat = r.split(' ').join('+');
      resolved.push(concat);
    });

    if (!resolved || resolved.length == 0) return '';
    var toReturn = '';
    var firstRound = true;
    resolved.forEach((rr) => {
      if (firstRound) {
        firstRound = false;
      } else {
        toReturn += '+';
      }
      toReturn += rr;
    });
    return toReturn;
  }

  //accetta il pattern da processare ed il tipo di oggetto da cercare in sessione
  //per risolvere il pattern stesso
  resolveMenuLinkPatternUsingSession(
    pattern: string,
    itemType: string,
    guid: string | null,
  ): string | null {
    if (!itemType) return null;
    if (!guid)
      guid =
        this.#routerService.routingData[
          this.#routerService.currentOutletsGuid['base']
        ].guid ?? '';
    var k = itemType;
    var item = this.#sessionService.get(k);
    if (item) {
      item = item['data'];
    }
    return this.resolveMenuLinkPattern(pattern, item);
  }

  //accetta il pattern dei parametri da processare ed il tipo di oggetto da cercare in sessione
  //per risolvere il pattern
  //params = "@id=$id|@company-bar=[companyName]|@ragione-sociale=sia informatica"
  //itemType = "crud-item"
  /**
   * @ngdoc method
   * @name resolveParamsPatternUsingSessionAsItem
   * @description resolve the pattern into session params. This method get session object by the given componentId and guid
   * @param {string} params the patter to resolve
   * @param {string} componentId to retrieve the session values. Session key = componentId + "-" + guid;
   * @param {string} guid to retrieve the session values. Session key = componentId + "-" + guid;
   * @returns SiaSessionParam[] } resolved pattern
   */
  resolveParamsPatternUsingSessionAsItem(
    params: string,
    componentId: string,
    guid: string,
  ): SiaSessionParam[] | null {
    if (!componentId) return null;
    var k = componentId + '-' + guid;
    var item = this.#sessionService.get(k);
    return this.resolveParamsPattern(params, item, guid);
  }

  /**
   * @ngdoc method
   * @name resolveParamsPattern
   * @description resolve the pattern into session params
   * @param {string} params the patter to resolve
   * @param {any} item to retrieve the [] values
   * @param {string} guid to retrieve the session values for $ pattern
   * @returns SiaSessionParam[]}resolved pattern
   */
  resolveParamsPattern(
    params: string,
    item: any,
    guid: string,
  ): SiaSessionParam[] {
    var res: SiaSessionParam[] = [];
    if (!params) return res;
    var parts = params.split('|');
    if (!parts || parts.length == 0) return res;
    parts.forEach((p) => {
      var paramDetails = p.split('=');
      if (paramDetails) {
        if (paramDetails.length == 1) {
          switch (paramDetails[0]) {
            case 'year':
            case 'currentYear':
              res.push({
                id: 'year',
                value: getYear(new Date()),
              });
              break;
          }
        } else if (paramDetails.length == 2) {
          var fieldName = paramDetails[0].split('@').join('');
          var paramValue = this.resolvePatternValue(
            paramDetails[1],
            item,
            guid,
          );
          if (fieldName) {
            res.push({ id: fieldName, value: paramValue });
          }
        }
      }
    });
    return res;
  }
  /**
   * @ngdoc method
   * @name getRouterParams
   * @description get the current route session params
   * @param {string} guid to retrieve the session values for $ pattern
   * @returns SiaSessionParam[]} the session parameters
   */
  getRouterParams() {
    var p = this.#sessionService.getSessionParams();
    return p;
  }
  /**
   * @ngdoc method
   * @name checkParamPattern
   * @description check if the values are the same, return true if one params is equals
   * @param {string} pattern the patter to check
   * @param {any} item to retrieve the [] values
   * @param {string} guid to retrieve the session values for $ pattern
   * @returns {boolean} return the check result
   */
  validateConditionParamPattern(
    pattern: string,
    item: any,
    guid: string,
  ): boolean {
    var res: boolean = false;
    if (!pattern) return res;
    if (pattern.includes('|')) {
      var parts = pattern.split('||');
      if (!parts || parts.length == 0) return res;
      parts.forEach((p) => {
        if (!res) res = this.checkConditions(p, item, guid);
      });
      return res;
    }
    if (pattern.includes('&')) {
      var parts = pattern.split('&&');
      if (!parts || parts.length == 0) return res;
      res = true;
      parts.forEach((p) => {
        if (res) res = this.checkConditions(p, item, guid);
      });
      return res;
    }

    return this.checkConditions(pattern, item, guid);
  }

  /**
   * @ngdoc method
   * @name checkConditions
   * @description check if the give condition is true
   * @param {string} pattern the patter to check
   * @param {any} item to retrieve the [] values
   * @param {string} guid to retrieve the session values for $ pattern
   * @returns {boolean} return the check result
   * @returns {any} the object for resolve the []
   */
  private checkConditions(pattern: string, item: any, guid: string): boolean {
    pattern = pattern.toLowerCase();
    var paramDetails = pattern.split('!=');
    if (paramDetails && paramDetails.length == 2) {
      var { fieldValue, paramValue } = this.extractParamAndFieldValue(
        paramDetails,
        item,
        guid,
      );
      return fieldValue != paramValue;
    }
    paramDetails = pattern.split('==');
    if (paramDetails && paramDetails.length == 2) {
      var { fieldValue, paramValue } = this.extractParamAndFieldValue(
        paramDetails,
        item,
        guid,
      );
      return fieldValue == paramValue;
    }
    paramDetails = pattern.split('<=');
    if (paramDetails && paramDetails.length == 2) {
      var { fieldValue, paramValue } = this.extractParamAndFieldValue(
        paramDetails,
        item,
        guid,
      );
      return fieldValue <= paramValue;
    }
    paramDetails = pattern.split('>=');
    if (paramDetails && paramDetails.length == 2) {
      var { fieldValue, paramValue } = this.extractParamAndFieldValue(
        paramDetails,
        item,
        guid,
      );
      return fieldValue >= paramValue;
    }
    paramDetails = pattern.split('<');
    if (paramDetails && paramDetails.length == 2) {
      var { fieldValue, paramValue } = this.extractParamAndFieldValue(
        paramDetails,
        item,
        guid,
      );
      return fieldValue < paramValue;
    }
    paramDetails = pattern.split('>');
    if (paramDetails && paramDetails.length == 2) {
      var { fieldValue, paramValue } = this.extractParamAndFieldValue(
        paramDetails,
        item,
        guid,
      );
      return fieldValue > paramValue;
    }
    paramDetails = pattern.split('contains');
    if (paramDetails && paramDetails.length == 2) {
      var { fieldValue, paramValue } = this.extractParamAndFieldValue(
        paramDetails,
        item,
        guid,
      );
      var value: string = '' + fieldValue;
      return value.includes(paramValue);
    }
    return false;
  }

  private extractParamAndFieldValue(
    paramDetails: string[],
    item: any,
    guid: string,
  ) {
    var paramValue: any;
    if (paramDetails[1] != 'undefined') {
      paramValue = this.resolvePatternValue(paramDetails[1], item, guid);
    } else {
      paramValue = undefined;
    }
    var fieldValue = this.resolvePatternValue(paramDetails[0], item, guid);
    return { fieldValue, paramValue };
  }

  convertInSessionParams(ff: {
    controlId: string;
    prefilters: any[];
  }): SiaSessionParam[] {
    var res: any[] = [];
    if (ff && ff.prefilters) {
      res = ff.prefilters
        .filter((filter) => !filter.skip)
        .map((pref) => {
          var f = pref.field;
          if (f) f = f.toLowerCase();
          if (!pref.value && pref.value != false) {
            pref.value = null;
          }
          var sp: SiaSessionParam = {
            id: pref.widget ? pref.widget : f,
            field: f,
            operator: pref.operator,
            value: pref.dataType == 'date' ? new Date(pref.value) : pref.value,
          };
          return sp;
        });
    }
    return res;
  }

  updateSiaSessionParamsWithGuid(
    sessionParams: SiaSessionParam[],
    guid: string | null,
  ) {
    if (!sessionParams || sessionParams.length == 0) return;

    let verifiedGuid: string = '';
    if (!guid) {
      guid =
        this.#routerService.routingData[
          this.#routerService.currentOutletsGuid['base']
        ].guid ?? '';
      verifiedGuid = guid ?? '';
    } else {
      verifiedGuid = guid;
    }
    var actuaSiaSessionParams =
      this.#sessionService.getSessionParams(verifiedGuid);
    if (!actuaSiaSessionParams) {
      this.#sessionService.updateSessionParams(verifiedGuid, sessionParams);
      return;
    }

    var paramsUpdated: SiaSessionParam[] = [...sessionParams];
    var newIds = paramsUpdated.map((param) => {
      return param.id;
    });
    actuaSiaSessionParams.forEach((actualParam) => {
      if (newIds.indexOf(actualParam.id) == -1) {
        paramsUpdated.push(actualParam);
      }
    });
    if (paramsUpdated && paramsUpdated.length > 0) {
      this.#sessionService.updateSessionParams(verifiedGuid, paramsUpdated);
    }
  }
}
